import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePrimaryColor,
} from '@vibesites/runtime/react';
import {
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import {
  MultihubLogoMonoWhite,
  MultihubLogoMonoBlack,
  repliWhiteLogoUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
import {
  formatDisplayPhone,
  condenseOfficeHours,
  type OfficeHourEntry,
} from './PropertyInfoPanel';
// Local black variant of the Repli wordmark — `@vibesites/assets` only ships
// `repliWhiteLogoUrl`, so we keep a dark-fill copy alongside it for use on
// light primary backgrounds. Source SVG mirrors `repli_white.svg` with the
// wordmark's `fill:#fff` swapped to `#1a1a1a` (matches the dark color used
// by the tokens generator's `getContrastTextColor`).
import repliBlackLogoUrl from '../assets/repli_black.svg';
// Gold geometric diamond pattern shown as a horizontal band above the
// footer. Sourced from the Oasis demo theme reference
// (`oasis-demo-theme.multiscreensite.com`) — the example renders the
// same image with `background-size: 20%; background-repeat: repeat`,
// which we mirror in CSS below so the tile reads at the same scale.
import footerPatternUrl from '../assets/footer-pattern.png';

// ============================================================================
// Contrast text color
// ----------------------------------------------------------------------------
// Mirrors `getContrastTextColor` from `@vibesites/tokens` (defined in
// `vendor/vibesites/tokens/generator.ts`, currently module-private and not
// re-exported, so we replicate the exact logic here verbatim — including the
// `0.55` luminance threshold and the `'#ffffff'` / `'#1a1a1a'` return values).
// Using the same function keeps logo color choice (white vs black) in lockstep
// with the auto-computed `--vibe-color-text-on-primary` CSS variable, so the
// footer's text and logos always pick the same contrast side.
// ============================================================================

const TEXT_ON_PRIMARY_LIGHT = '#ffffff';
const TEXT_ON_PRIMARY_DARK = '#1a1a1a';

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Returns the contrasting text color (`'#ffffff'` or `'#1a1a1a'`) for a given
 * background hex. Identical to `getContrastTextColor` in `@vibesites/tokens`.
 * Falls back to the white-on-dark side when the input can't be parsed, which
 * matches how the tokens generator treats unknown / missing primary colors.
 */
function getContrastTextColor(bgHex: string | null | undefined): string {
  if (!bgHex) return TEXT_ON_PRIMARY_LIGHT;
  const luminance = getRelativeLuminance(bgHex);
  // Favor white text on medium tones (gold, tan, dusty blue) where dark text is
  // technically higher-contrast but reads as heavy/muddy. Flip to dark only on
  // genuinely light backgrounds (pastels, cream, near-white).
  return luminance > 0.55 ? TEXT_ON_PRIMARY_DARK : TEXT_ON_PRIMARY_LIGHT;
}

// ============================================================================
// Hours label formatters (footer-only)
// ----------------------------------------------------------------------------
// The shared `condenseOfficeHours` helper returns day labels straight from the
// data (often stored as "MONDAY"/"FRIDAY") and `formatTime` produces uppercase
// "AM"/"PM" suffixes. We don't want the footer to display either as ALL CAPS
// — title case for day names, lowercase for the meridiem — so we normalize
// the strings just before rendering. Scoped here (not in
// `PropertyInfoPanel`) so the contact/tour panels are unaffected.
// CSS `text-transform: capitalize` can't lowercase the inner letters of
// already-uppercase input, so the transform has to live in JS.
// ============================================================================

function titleCaseDayLabel(str: string): string {
  // Lowercase the whole string, then re-uppercase the first letter of each
  // word. Handles single days ("MONDAY" → "Monday") and ranges with the
  // en-dash separator used by `condenseOfficeHours` ("MON – FRI" → "Mon – Fri").
  return str.replace(/[A-Za-z]+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
}

function lowercaseMeridiem(str: string): string {
  // Just flip the AM/PM markers — keep digits, spaces, and the en-dash intact.
  return str.replace(/\b(AM|PM)\b/g, (s) => s.toLowerCase());
}

// ============================================================================
// Slot Group Definitions
// ----------------------------------------------------------------------------
// Schema-linter rule (`prefer-module-level-defs`) requires SlotGroupDefinitions
// to live as module-level literals — keep all of them up here so the linter
// can statically extract them.
// ============================================================================

// ── Brand logo (top of the left column) ──────────────────────────────────
// Picks the property's mono-white logo by default so it reads on the dark
// primary band. We swap to mono_black at runtime when the primary color is
// light (see `getContrastTextColor` below).
const brandLogoDef: SlotGroupDefinition = {
  label: 'Brand Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: [
        'property_mono_white_logo',
        'property_mono_black_logo',
        'property_primary_logo',
        'property_secondary_logo',
      ],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property Logo',
    },
    url: { label: 'URL', type: 'url', defaultValue: '/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ── Social links (Facebook, Instagram) ───────────────────────────────────
// Sourced from the property's social URL fields so the icons disappear
// when the property hasn't configured a handle.
const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ── Text Us link (between address and phone, mirrors Lake Haus) ──────────
// No `defaultField` — properties don't have a canonical SMS-widget field,
// so editors paste the URL from their texting provider. Hidden in the
// rendered footer when no URL is set.
const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '' },
    label: { label: 'Display Text', type: 'text', defaultValue: 'Text Us!' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ── Footer CTA (the "Furnish Your Home with CORT" button under hours) ────
// Generic editable button — defaults to the CORT URL used by the Lake Haus
// reference so it appears with sensible content out of the box. Editors
// can clear the URL to hide it, or repurpose it for another partner CTA.
const ctaButtonDef: SlotGroupDefinition = {
  label: 'Footer CTA Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '' },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};



// ============================================================================
// Component
// ============================================================================

export function Footer() {
  // ---- Contrast-driven logo variant selection ----
  // Decide whether text-on-primary should be white or dark using the same
  // `getContrastTextColor` logic the tokens generator uses to compute
  // `--vibe-color-text-on-primary`. We then pick mono_white / mono_black logo
  // defaults (and the Multihub + Repli marks) to match that decision, so the
  // footer logos always sit on the correct contrast side regardless of how
  // dark/light the property's primary brand color is. The runtime-adjusted
  // slot definitions are passed to both `useSlotGroup` and `EditableSlotGroup`
  // so the editor surfaces the same default the renderer is using.
  const primaryColor = usePrimaryColor();
  const textOnPrimary = getContrastTextColor(primaryColor);
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  // Flip the brand logo + managed-by logo defaults to the mono_black variant
  // on light primary backgrounds, mirroring what the attribution logos do.
  const runtimeBrandLogoDef: SlotGroupDefinition = useWhiteOnPrimary
    ? brandLogoDef
    : {
        ...brandLogoDef,
        fields: {
          ...brandLogoDef.fields,
          image: {
            ...brandLogoDef.fields.image,
            defaultField: 'property_mono_black_logo',
          },
        },
      };

  const runtimeManagedByLogoDef: SlotGroupDefinition = useWhiteOnPrimary
    ? managedByLogoDef
    : {
        ...managedByLogoDef,
        fields: {
          ...managedByLogoDef.fields,
          image: {
            ...managedByLogoDef.fields.image,
            defaultField: 'property_company_mono_black_logo',
          },
        },
      };

  // ── Top contact block (logo / contact / hours) ──────────────────────────
  const brandLogo = useSlotGroup('footer.brand_logo', runtimeBrandLogoDef);
  const facebook = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const textUs = useSlotGroup('footer.text_us_link', textUsLinkDef);
  const ctaButton = useSlotGroup('footer.cta_button', ctaButtonDef);

  const address = useContentToken('footer.address');
  const phone = useContentToken('footer.phone') || '';
  const phoneDisplay = phone ? formatDisplayPhone(phone) : '';
  // Build a Google Maps search URL from the editor-resolved address. The
  // vendored runtime in this theme doesn't ship `usePropertyAddressUrl`,
  // so we encode the address with `?api=1&query=` ourselves — this opens
  // the native maps app on mobile and Google Maps on desktop, matching
  // what the runtime helper would produce.
  const addressUrl = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : '';

  const officeHours = useCollectionBinding<OfficeHourEntry>('footer.office_hours', {
    collection: 'office_hours',
  });
  const hasHours = officeHours.length > 0;
  const hoursRows = hasHours ? condenseOfficeHours(officeHours) : [];

  const hasAnySocial = Boolean(facebook.url || instagram.url);

  // ── Attribution row ─────────────────────────────────────────────────────
  const managedByLogo = useSlotGroup('footer.managed_by_logo', runtimeManagedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  return (
    <>
      {/* ── Decorative pattern row (sits above the primary-color footer
            band on every page). The bitmap is rendered as a tiled
            background-image at the same scale the reference site uses
            (`background-size: 20%; background-repeat: repeat;
            background-position: 50% 0`), so the diamond tile reads bold
            and large rather than shrunk into a fine line texture. */}
      <div
        className="footer-pattern-row"
        aria-hidden="true"
        role="presentation"
      />

      <footer
        className="text-primary-foreground footer-root"
        style={{ backgroundColor: 'var(--vibe-color-primary)' }}
      >
        {/* ────────────────────────────────────────────────────────────────
            TOP ROW — 3 columns at desktop, stacked on mobile.
              Col 1: Brand logo + copyright + social icons
              Col 2: Contact Us heading + address + Text Us! + phone
              Col 3: Office Hours heading + day/time rows + CTA button
            ──────────────────────────────────────────────────────────── */}
        <div className="footer-top max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="footer-top-grid">

            {/* ---- Col 1: Brand ---- */}
            <div className="footer-brand-col">
              <EditableSlotGroup groupPath="footer.brand_logo" definition={runtimeBrandLogoDef}>
                {brandLogo.image ? (
                  <a
                    href={brandLogo.url || '/'}
                    target={brandLogo._target || '_self'}
                    className="footer-brand-link"
                    aria-label={brandLogo.alt || 'Home'}
                  >
                    <VibesitesImage
                      url={brandLogo.image}
                      alt={brandLogo.alt || 'Property Logo'}
                      className="footer-brand-logo"
                    />
                  </a>
                ) : (
                  <div className="footer-brand-fallback" aria-hidden="true" />
                )}
              </EditableSlotGroup>

              {/* Copyright sits under the logo on the left, matching Lake
                  Haus. Year auto-updates from the runtime clock so the
                  footer never goes stale. */}
              <p className="footer-copyright">
                © {new Date().getFullYear()} All Rights Reserved.
              </p>

              {hasAnySocial && (
                <div className="footer-social-row" role="list" aria-label="Social links">
                  {facebook.url && (
                    <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                      <a
                        href={facebook.url}
                        target={facebook._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={facebook.label || 'Facebook'}
                        className="footer-social-link"
                        role="listitem"
                      >
                        <FontAwesomeIcon icon={faFacebook} className="footer-social-icon" />
                      </a>
                    </EditableSlotGroup>
                  )}
                  {instagram.url && (
                    <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                      <a
                        href={instagram.url}
                        target={instagram._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={instagram.label || 'Instagram'}
                        className="footer-social-link"
                        role="listitem"
                      >
                        <FontAwesomeIcon icon={faInstagram} className="footer-social-icon" />
                      </a>
                    </EditableSlotGroup>
                  )}
                </div>
              )}
            </div>

            {/* ---- Col 2: Contact Us ---- */}
            <div className="footer-col">
              <RichTextContent
                path="footer.contact_heading"
                defaultValue="<p>Contact Us</p>"
                as="h3"
                className="footer-col-heading"
              />

              <div className="footer-contact-list">
                {/* Address — linked to Google Maps when set. No icon, plain
                    stacked text per the Lake Haus reference. */}
                {address ? (
                  addressUrl ? (
                    <a
                      href={addressUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer-contact-link"
                    >
                      <EditableToken path="footer.address" as="span">
                        {address}
                      </EditableToken>
                    </a>
                  ) : (
                    <EditableToken path="footer.address" as="p" className="footer-contact-line">
                      {address}
                    </EditableToken>
                  )
                ) : null}

                {/* Text Us! — only renders when the editor has filled in a
                    URL for the property's SMS widget. Underlined to match
                    the reference (signals "click me"). */}
                {textUs.url && (
                  <EditableSlotGroup groupPath="footer.text_us_link" definition={textUsLinkDef}>
                    <a
                      href={textUs.url}
                      target={textUs._target || '_blank'}
                      rel="noopener noreferrer"
                      className="footer-contact-link footer-textus-link"
                    >
                      {textUs.label || 'Text Us!'}
                    </a>
                  </EditableSlotGroup>
                )}

                {/* Phone — tel: link, dni hooks preserved for call-tracking. */}
                {phone && (
                  <a
                    href={`tel:${phone}`}
                    className="dniphone dniphonehref footer-contact-link"
                  >
                    <EditableToken path="footer.phone" as="span">
                      {phoneDisplay}
                    </EditableToken>
                  </a>
                )}
              </div>
            </div>

            {/* ---- Col 3: Office Hours + CTA ---- */}
            <div className="footer-col">
              <RichTextContent
                path="footer.hours_heading"
                defaultValue="<p>Office Hours</p>"
                as="h3"
                className="footer-col-heading"
              />

              {hasHours && (
                <div className="footer-hours-grid">
                  {hoursRows.map((row, i) => (
                    <div key={i} className="footer-hours-row">
                      <span className="footer-hours-day">{titleCaseDayLabel(row.dayLabel)}</span>
                      <span className="footer-hours-time">{lowercaseMeridiem(row.timeLabel)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* CTA button below the hours — hidden when no URL is set so
                  the column gracefully collapses to "Hours only" for
                  properties without a partner CTA. Uses the secondary
                  color to pop against the primary band. */}
              {ctaButton.url && (
                <EditableSlotGroup groupPath="footer.cta_button" definition={ctaButtonDef}>
                  <a
                    href={ctaButton.url}
                    target={ctaButton._target || '_blank'}
                    rel="noopener noreferrer"
                    className="footer-cta-button"
                  >
                    {ctaButton.label || 'Learn More'}
                  </a>
                </EditableSlotGroup>
              )}
            </div>

          </div>
        </div>

        {/* Hairline divider between the top contact block and the
            compliance/attribution band below. */}
        <div className="footer-divider" aria-hidden="true" />

        {/* ────────────────────────────────────────────────────────────────
            COMPLIANCE ICONS — centered standalone row.
            Wheelchair / Fair Housing / Pets icons, low-opacity contrast
            color so they sit quietly between the contact block and the
            attribution row.
            ──────────────────────────────────────────────────────────── */}
        <div className="footer-compliance-row" aria-label="Compliance">
          <WheelchairIcon size={24} color="currentColor" />
          <FairHousingIcon size={24} color="currentColor" />
          <PetsAllowedIcon size={24} color="currentColor" />
        </div>

        {/* ────────────────────────────────────────────────────────────────
            ATTRIBUTION ROW — 3 columns at desktop, stacked centered on mobile.
              Left:   Professionally Managed by + property company logo
              Center: Legal links · Accessibility · Privacy · Sitemap
                     + Powered by Multihub
              Right:  Website Design by + Repli
            ──────────────────────────────────────────────────────────── */}
        <div className="footer-attribution max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="footer-attribution-grid">

            {/* ---- Attribution left: Managed By ---- */}
            <div className="footer-attribution-col footer-attribution-left">
              {managedByLogo.image ? (
                <EditableSlotGroup groupPath="footer.managed_by_logo" definition={runtimeManagedByLogoDef}>
                  <div className="footer-attribution-item">
                    <RichTextContent
                      path="footer.managed_by_text"
                      defaultValue="<p>Professionally Managed by</p>"
                      as="span"
                      className="footer-attribution-label"
                    />
                    <a
                      href={managedByLogo.url || '#'}
                      target={managedByLogo._target || '_blank'}
                      rel="noopener noreferrer"
                      className="footer-attribution-logo-link"
                    >
                      <VibesitesImage
                        url={managedByLogo.image}
                        alt={managedByLogo.alt || 'Property Management Company'}
                        className="footer-attribution-logo"
                      />
                    </a>
                  </div>
                </EditableSlotGroup>
              ) : null}
            </div>

            {/* ---- Attribution center: Legal links + Powered by ---- */}
            <div className="footer-attribution-col footer-attribution-center">
              <nav className="footer-legal-nav" aria-label="Legal links">
                <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                  <a
                    href={accessibilityLink.url || '/accessibility'}
                    target={accessibilityLink._target || '_self'}
                    className="footer-legal-link"
                  >
                    {accessibilityLink.text || 'Accessibility'}
                  </a>
                </EditableSlotGroup>
                <span aria-hidden="true" className="footer-legal-sep">·</span>
                <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                  <a
                    href={privacyPolicyLink.url || '/privacy-policy'}
                    target={privacyPolicyLink._target || '_self'}
                    className="footer-legal-link"
                  >
                    {privacyPolicyLink.text || 'Privacy Policy'}
                  </a>
                </EditableSlotGroup>
                <span aria-hidden="true" className="footer-legal-sep">·</span>
                <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                  <a
                    href={sitemapLink.url || '/sitemap'}
                    target={sitemapLink._target || '_self'}
                    className="footer-legal-link"
                  >
                    {sitemapLink.text || 'Sitemap'}
                  </a>
                </EditableSlotGroup>
              </nav>

              <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
                <div className="footer-attribution-item">
                  <RichTextContent
                    path="footer.powered_by_text"
                    defaultValue="<p>Powered by</p>"
                    as="span"
                    className="footer-attribution-label"
                  />
                  <a
                    href={poweredByLogo.url || 'https://www.multihub.io/'}
                    target={poweredByLogo._target || '_blank'}
                    rel="noopener noreferrer"
                    className="footer-attribution-logo-link"
                  >
                    {useWhiteOnPrimary ? (
                      <MultihubLogoMonoWhite height={22} alt={poweredByLogo.alt || 'Multihub'} />
                    ) : (
                      <MultihubLogoMonoBlack height={22} alt={poweredByLogo.alt || 'Multihub'} />
                    )}
                  </a>
                </div>
              </EditableSlotGroup>
            </div>

            {/* ---- Attribution right: Designed By ---- */}
            <div className="footer-attribution-col footer-attribution-right">
              <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
                <div className="footer-attribution-item">
                  <RichTextContent
                    path="footer.design_by_text"
                    defaultValue="<p>Website Design by</p>"
                    as="span"
                    className="footer-attribution-label"
                  />
                  <a
                    href={designByLogo.url || 'https://www.repli360.com/'}
                    target={designByLogo._target || '_blank'}
                    rel="noopener noreferrer"
                    className="footer-attribution-logo-link"
                  >
                    <VibesitesImage
                      url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                      alt={designByLogo.alt || 'Repli'}
                      height={28}
                      className="footer-attribution-repli"
                      priority
                    />
                  </a>
                </div>
              </EditableSlotGroup>
            </div>

          </div>

          {/* Identity verification notice — final centered line. Editors
              can replace it with property-specific compliance language
              via the richtext token. */}
          <RichTextContent
            path="footer.verification_notice"
            defaultValue="<p>We participate in identity verification of all applicants.</p>"
            as="p"
            className="footer-verification"
          />
        </div>

      </footer>

      <style>{`
        /* ============================================================
           Footer pattern row — horizontal band that sits above the
           dark primary-color footer on every page. Renders the gold
           geometric diamond pattern PNG (imported as
           \`footerPatternUrl\`) as a tiled background-image.
           ============================================================ */
        .footer-pattern-row {
          width: 100%;
          background-color: var(--vibe-color-background, #ffffff);
          background-image: url(${footerPatternUrl});
          background-repeat: repeat;
          background-size: 35%;
          background-position: 50% 0;
          padding: 22px 40px;
          line-height: 0;
        }
        @media (min-width: 768px) {
          .footer-pattern-row { padding: 28px 40px; }
        }
        @media (min-width: 1200px) {
          .footer-pattern-row { padding: 34px 40px; }
        }

        /* ============================================================
           TOP ROW — 3-column grid (logo / contact / hours).
           Stacks vertically and centers on mobile; switches to a 3-col
           grid at md (768px) and left-aligns each column.
           ============================================================ */
        .footer-top {
          padding-top: 1.75rem;
          padding-bottom: 2rem;
          color: var(--vibe-color-text-on-primary, #ffffff);
        }
        @media (min-width: 768px) {
          .footer-top {
            padding-top: 2.25rem;
            padding-bottom: 2.5rem;
          }
        }
        /* Content is centered at every breakpoint — the reference keeps
           the brand/contact/hours columns visually centered within their
           grid cells even on wide desktop, rather than left/right-aligning
           against the column edges. */
        .footer-top-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: start;
          text-align: center;
          justify-items: center;
        }
        @media (min-width: 768px) {
          .footer-top-grid {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
            gap: 2.5rem;
          }
        }
        @media (min-width: 1024px) {
          .footer-top-grid { gap: 3.5rem; }
        }

        /* ── Col 1: Brand (logo + copyright + social) ──
           Uses an extra-tight column gap so the copyright sits flush
           under the logo (it reads as a caption to the mark). The
           social row restores its own breathing room via
           .footer-social-row's margin-top, so only the logo→copyright
           pair is tightened. */
        .footer-brand-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }
        .footer-brand-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
        }
        .footer-brand-logo {
          display: block;
          width: auto;
          max-width: 220px;
          max-height: 110px;
          height: auto;
          object-fit: contain;
        }
        @media (min-width: 768px) {
          .footer-brand-logo {
            max-width: 240px;
            max-height: 130px;
          }
        }
        .footer-brand-fallback {
          width: 180px;
          height: 60px;
          background: color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 8%, transparent);
        }
        .footer-copyright {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.5;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.75;
          margin: 0;
        }

        /* Social row — circular icon buttons under the logo/copyright.
           Larger margin-top here (vs the column's small gap) keeps the
           social icons visually separated from the copyright while the
           logo→copyright pair remains tight. */
        .footer-social-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.75rem;
        }
        .footer-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.25rem;
          height: 2.25rem;
          border-radius: 9999px;
          background: color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 12%, transparent);
          color: var(--vibe-color-text-on-primary, #ffffff);
          text-decoration: none;
          transition: background-color 0.2s ease, transform 0.2s ease;
        }
        .footer-social-link:hover,
        .footer-social-link:focus-visible {
          background: color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 22%, transparent);
          transform: translateY(-1px);
        }
        .footer-social-link:focus-visible {
          outline: 2px solid var(--vibe-color-text-on-primary, #ffffff);
          outline-offset: 2px;
        }
        .footer-social-icon {
          width: 1.05rem;
          height: 1.05rem;
        }

        /* ── Cols 2 & 3: Generic column wrapper ──
           Columns stay center-aligned at every breakpoint, matching the
           Lake Haus reference where each column reads as a centered
           label-and-body block. */
        .footer-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          width: 100%;
        }
        /* Column headings ("Contact Us", "Office Hours") — kept on the
           body font so the upper section reads as a single typographic
           family (heading font would create a visible mismatch against
           the address / phone / hours rows below). Weight 700 makes the
           labels read as bold section titles against the regular-weight
           body text underneath. */
        .footer-col-heading {
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          line-height: 1.3;
          font-weight: 700;
          letter-spacing: 0;
          margin: 0 0 0.25rem 0;
          color: var(--vibe-color-text-on-primary, #ffffff);
        }
        /* Strip default paragraph margins added by RichTextContent so the
           heading sits tight against the body text underneath. */
        .footer-col-heading p { margin: 0; }

        /* ── Contact list (address / text-us / phone, no icons) ──
           Stacked, centered text per the reference. Each item is a
           plain link/paragraph — no bullets, no icons. */
        .footer-contact-list {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          width: 100%;
          align-items: center;
        }
        .footer-contact-line,
        .footer-contact-link {
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          line-height: 1.5;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.9;
          text-decoration: none;
          margin: 0;
          transition: opacity 0.2s ease;
          white-space: normal;
          text-align: center;
        }
        .footer-contact-link:hover,
        .footer-contact-link:focus-visible {
          opacity: 1;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        /* "Text Us!" is shown as an inline-styled link by default so it
           reads as an action even before hover, matching the Lake Haus
           reference. */
        .footer-textus-link {
          text-decoration: underline;
          text-underline-offset: 3px;
          align-self: center;
        }

        /* ── Office hours grid (day on left, time on right) ──
           Narrow capped width pulls the day and time columns close
           together while preserving the space-between tabular alignment
           (each row's day stays aligned vertically with the row above,
           same for the time column). */
        .footer-hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          width: 100%;
          max-width: 200px;
          margin: 0 auto;
        }
        @media (min-width: 768px) {
          .footer-hours-grid { max-width: 220px; }
        }
        .footer-hours-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          line-height: 1.5;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.9;
        }
        /* Day name and time use the same weight per the reference — the
           visual separation comes from the space-between alignment on
           the row, not from a weight contrast. Keeping them matched
           means the row reads as a single tabular pair, not label+value. */
        .footer-hours-day { font-weight: 400; }
        .footer-hours-time { font-weight: 400; }

        /* ── Footer CTA button (under hours) ──
           Reference button is a solid tan rectangle with a regular-
           weight body-font label (not heading-bold). Matching the
           reduced text scale of the surrounding contact/hours block. */
        .footer-cta-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-top: 0.75rem;
          padding: 0.75rem 1.25rem;
          background: var(--vibe-color-secondary, var(--vibe-color-primary));
          color: var(--vibe-color-text-on-secondary, var(--vibe-color-text-on-primary, #ffffff));
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          font-weight: 500;
          letter-spacing: 0;
          text-decoration: none;
          border: 0;
          transition: filter 0.2s ease, transform 0.2s ease;
          width: 100%;
          max-width: 280px;
        }
        @media (min-width: 768px) {
          .footer-cta-button { max-width: 300px; }
        }
        .footer-cta-button:hover,
        .footer-cta-button:focus-visible {
          filter: brightness(1.05);
          transform: translateY(-1px);
        }
        .footer-cta-button:focus-visible {
          outline: 2px solid var(--vibe-color-text-on-primary, #ffffff);
          outline-offset: 2px;
        }

        /* ── Divider between top contact block and compliance/attribution ── */
        .footer-divider {
          max-width: 80rem;
          margin: 0 auto;
          height: 3px;
          background: var(--vibe-color-text-on-primary, #ffffff);
        }

        /* ── Compliance icons — centered standalone row ── */
        .footer-compliance-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2rem;
          padding: 1.75rem 1rem;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.7;
        }
        @media (min-width: 768px) {
          .footer-compliance-row {
            gap: 2.5rem;
            padding: 2rem 1rem;
          }
        }

        /* ============================================================
           ATTRIBUTION ROW — 3 columns at desktop.
              Left:   Professionally Managed by + logo
              Center: Legal links + Powered by Multihub
              Right:  Website Design by + Repli
           ============================================================ */
        .footer-attribution {
          padding-bottom: 1.75rem;
        }
        .footer-attribution-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          align-items: center;
          text-align: center;
          justify-items: center;
        }
        @media (min-width: 768px) {
          .footer-attribution-grid {
            grid-template-columns: 1fr 1fr 1fr;
            gap: 1.5rem;
            align-items: center;
          }
          .footer-attribution-left {
            justify-self: start;
            text-align: left;
          }
          .footer-attribution-center {
            justify-self: center;
            text-align: center;
          }
          .footer-attribution-right {
            justify-self: end;
            text-align: right;
          }
        }
        .footer-attribution-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }
        @media (min-width: 768px) {
          .footer-attribution-left { align-items: flex-start; }
          .footer-attribution-right { align-items: flex-end; }
        }
        /* Each attribution item stacks the label above its logo per the
           reference (e.g. "Professionally Managed by" on its own line,
           then the Weidner logo below). Column-level alignment classes
           handle horizontal placement; here we just stack vertically. */
        .footer-attribution-item {
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          gap: 0.4rem;
        }
        @media (min-width: 768px) {
          .footer-attribution-left .footer-attribution-item { align-items: flex-start; }
          .footer-attribution-right .footer-attribution-item { align-items: flex-end; }
        }
        .footer-attribution-label {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.4;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.7;
        }
        /* Strip any default paragraph margins from RichTextContent so
           the label and logo stay tightly stacked. */
        .footer-attribution-label p { margin: 0; }
        .footer-attribution-logo-link {
          display: inline-flex;
          align-items: center;
          opacity: 0.9;
          transition: opacity 0.2s ease;
        }
        .footer-attribution-logo-link:hover,
        .footer-attribution-logo-link:focus-visible {
          opacity: 1;
        }
        .footer-attribution-logo {
          height: 32px;
          width: auto;
          max-width: 180px;
          object-fit: contain;
          display: block;
        }
        .footer-attribution-repli {
          height: 28px;
          width: auto;
          display: block;
        }

        /* Legal nav (Accessibility · Privacy · Sitemap) — sits above the
           Powered By logo in the center column. */
        .footer-legal-nav {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.5;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.85;
        }
        .footer-legal-link {
          color: inherit;
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        .footer-legal-link:hover,
        .footer-legal-link:focus-visible {
          opacity: 0.7;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .footer-legal-sep {
          opacity: 0.5;
        }

        /* ── Identity verification notice ── */
        .footer-verification {
          margin: 1.75rem auto 0;
          max-width: 36rem;
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.6;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.7;
        }
        .footer-verification p { margin: 0; }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    // ── Top contact block ─────────────────────────────────────────────────
    'footer.contact_heading': {
      label: 'Contact Heading',
      type: 'richtext',
      defaultValue: '<p>Contact Us</p>',
    },
    'footer.hours_heading': {
      label: 'Hours Heading',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.address': {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'footer.verification_notice': {
      label: 'Identity Verification Notice',
      type: 'richtext',
      defaultValue: '<p>We participate in identity verification of all applicants.</p>',
    },
    // ── Attribution row labels ────────────────────────────────────────────
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
  },
  slotGroups: {
    // ── Top contact block ─────────────────────────────────────────────────
    'footer.brand_logo': brandLogoDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.text_us_link': textUsLinkDef,
    'footer.cta_button': ctaButtonDef,
    // ── Attribution row ───────────────────────────────────────────────────
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
